import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Receipt, Send, Star, PhoneCall, Bot, Zap } from 'lucide-react';

const EVENTS = [
  { icon: Receipt, text: 'Invoice sent automatically', sub: 'Apex Plumbing · $3,200 · on job close', accent: true },
  { icon: Send,    text: 'Outreach email sent',        sub: '47 prospects · Houston bakeries',        accent: false },
  { icon: Star,    text: 'Review reply drafted',       sub: "Bella's Bakery · 5★ Google review",      accent: true },
  { icon: PhoneCall, text: 'Missed call → SMS sent',  sub: 'Urban Fitness · 8 seconds after miss',    accent: false },
  { icon: Zap,     text: 'AR reminder sent',           sub: 'Pacific Yoga · 21 days overdue',          accent: true },
  { icon: Bot,     text: 'Hot lead routed',            sub: 'Downtown Salon replied → forwarded',       accent: false },
  { icon: Receipt, text: 'Month-end close complete',   sub: '312 transactions · 4 accounts',           accent: true },
  { icon: Send,    text: 'Follow-up sequence sent',    sub: 'Cold email clicker · step 2 triggered',   accent: false },
];

function AgentActivityFeed() {
  const counterRef = useRef(EVENTS.length);
  const [feed, setFeed] = useState(
    EVENTS.slice(0, 5).map((e, i) => ({ ...e, id: i }))
  );

  useEffect(() => {
    const id = setInterval(() => {
      const next = EVENTS[counterRef.current % EVENTS.length];
      counterRef.current += 1;
      setFeed(prev => [{ ...next, id: counterRef.current }, ...prev.slice(0, 4)]);
    }, 2800);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="glass rounded-2xl p-5 border border-border shadow-glow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
          </span>
          <span className="text-xs text-text-muted font-medium uppercase tracking-wider">Agent activity</span>
        </div>
        <span className="text-xs text-text-muted">live</span>
      </div>

      <div className="space-y-2">
        {feed.map((event, i) => {
          const color = event.accent ? '#6366F1' : '#06B6D4';
          return (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: Math.max(0.3, 1 - i * 0.18), y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-3 p-3 rounded-xl bg-surface-2"
            >
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: color + '22' }}
              >
                <event.icon size={14} style={{ color }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-text font-medium leading-tight">{event.text}</p>
                <p className="text-xs text-text-muted truncate mt-0.5">{event.sub}</p>
              </div>
              <span className="text-xs text-text-muted flex-shrink-0 pl-2">
                {i === 0 ? 'now' : `${i * 3}m`}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative pt-28 pb-20 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-[300px] h-[300px] bg-accent-2/8 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="inline-flex items-center gap-2 glass px-4 py-1.5 rounded-full text-xs text-text-muted mb-7"
            >
              <Sparkles size={13} className="text-accent-2" />
              Agentic AI &amp; automation for small business
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="font-display font-bold text-6xl md:text-8xl tracking-tight text-text mb-6 leading-[1.02]"
            >
              Run your business.{' '}
              <span className="gradient-text">Let AI handle the rest.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.12 }}
              className="text-lg md:text-xl text-text-muted max-w-xl mb-9 leading-relaxed"
            >
              Digital workers that send invoices, chase overdue accounts, run marketing outreach, manage reviews, and close your books — built for your business, running 24/7 without you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-3"
            >
              <a
                href="#services"
                className="group bg-gradient-brand text-white px-7 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 hover:shadow-glow transition-shadow"
              >
                See what I automate
                <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
              </a>
              <a
                href="#contact"
                className="glass px-7 py-3.5 rounded-full font-semibold text-text hover:border-accent/40 transition-colors"
              >
                Book a free call
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mt-10 flex flex-wrap gap-x-7 gap-y-2.5 text-xs text-text-muted"
            >
              <span>Back-office automation</span>
              <span className="text-border">·</span>
              <span>Marketing &amp; outreach</span>
              <span className="text-border">·</span>
              <span>24/7 operation</span>
              <span className="text-border">·</span>
              <span>Built for your business</span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, delay: 0.15 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <AgentActivityFeed />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
